import { QuestionCircleTwoTone } from '@ant-design/icons';
import { Alert, ConfigProvider, Flex, Switch, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { ErrorListDisplay } from 'components/ErrorDisplay';
import { Title } from 'components/mvp-typography';
import { PrintableRootPage } from 'components/page/page-2';
import { CrxSearch } from 'components/search-component/search-component';
import { Role } from 'features/auth/types';
import { PatientInfo } from 'lib/api/firebase/apis/apis';
import { useGetPatientListWithAssessmentData } from 'lib/hooks/tanstack/use-getPatientsList';
import { useQueryErrorNotifier } from 'lib/hooks/use-query-error-notifier';
import { useClinicianContext } from 'lib/hooks/use-resolveCurrentClinicInfo';
import { useAppSelector } from 'lib/store/rootReducer';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { showRecordUnavailableModal } from 'lib/utils/modal-utils';
import { useErrorList } from 'lib/utils/useErrorList';
import { Moment } from 'moment';
import { PatientRoutePattern } from 'navigation/router/route-switch/route-switch-patient/utils/patient-route-helpers';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { AddPatientButton } from './_components/AddPatientButton';
import { AddPatientDrawer } from './_components/AddPatientDrawer';
import { SendAssessmentButton } from './_components/SendAssessmentButton';
import './patient-list.scss';
import { useIsInImPACTClinic } from './useIsInImPACTClinic';
import { useSearchQueryForPatientList } from './useSearchQuery';

const COLUMNS: ColumnsType<
  PatientInfo & {
    signUpDate: Moment;
    isPending?: boolean;
  }
> = [
  {
    key: 'name',
    title: 'Name',
    dataIndex: 'displayName',
    sorter: (a, b) => a.displayName.localeCompare(b.displayName),
    render: (val: string) => {
      if (val.length !== 0) return val;
      return (
        <Flex gap={'small'}>
          <span>N/A</span>
          <Tooltip title="Test taker has not completed their onboarding yet">
            <QuestionCircleTwoTone />
          </Tooltip>
        </Flex>
      );
    }
  },
  {
    key: 'email',
    title: 'Email',
    dataIndex: 'email',
    sorter: (a, b) => a.email.localeCompare(b.email),
    render: (val: string) => {
      if (val.length !== 0) return val;
      return (
        <Flex gap={'small'}>
          <span>N/A</span>
          <Tooltip title="Test taker has not completed their onboarding yet">
            <QuestionCircleTwoTone />
          </Tooltip>
        </Flex>
      );
    }
  },
  {
    key: 'passportId',
    title: (
      <Flex gap={'small'}>
        Passport ID{' '}
        <Tooltip title="An ImPACT Passport ID uniquely identifies your Test-Taker in the ImPACT system.">
          <QuestionCircleTwoTone />
        </Tooltip>
      </Flex>
    ),
    render: (_v, r) => {
      const val = r.__impactData__.passportId ?? '';
      if (val.length !== 0) return val;
      return (
        <Flex gap={'small'}>
          <span>N/A</span>
          <Tooltip title="Test taker has not completed their first ImPACT test yet">
            <QuestionCircleTwoTone />
          </Tooltip>
        </Flex>
      );
    }
  },
  // {
  //   key: 'signUpData',
  //   title: 'Sign up date',
  //   dataIndex: 'signUpDate',
  //   render: (val: moment.Moment) => val && val.format('YYYY-MM-DD'),
  //   sorter: (a, b) =>
  //     Number(a.signUpDate?.unix() ?? 0) - Number(b.signUpDate?.unix() ?? 0)
  // },
  {
    key: 'lastCompletedAssessment',
    title: 'Date of last completed assessment',
    render: (_, record) => {
      const date = record.__assessmentData__.lastCompletedAssessmentDate;
      return date === null || date === undefined
        ? ''
        : date.format('YYYY-MM-DD');
    },
    sorter: (a, b) =>
      Number(a.__assessmentData__.lastCompletedAssessmentDate?.unix() ?? 0) -
      Number(b.__assessmentData__.lastCompletedAssessmentDate?.unix() ?? 0)
  },
  {
    key: 'pendingAssessment',
    title: 'Pending assessment',
    render: (_, a) => a.__assessmentData__.pendingAssessmentType
  }
  // {
  //   key: 'recentAssessmentDate',
  //   title: 'Recent assessment date',
  //   dataIndex: 'recentAssessmentDate',
  //   render: (val: moment.Moment | null) =>
  //     val === null ? val : val.format('YYYY-MM-DD'),
  //   sorter: (a, b) =>
  //     Number(a.recentAssessmentDate?.unix() ?? 0) -
  //     Number(b.recentAssessmentDate?.unix() ?? 0)
  // }
];

/**
 * Displays a list of available test-taker profiles in a table
 */
export default function ClinicianHomeV2() {
  const errorList = useErrorList();

  const history = useHistory();
  const clinicianContext = useClinicianContext();
  const { user } = useAppSelector(s => s.auth);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<PatientInfo[]>([]);

  const confirmedUsers = useGetPatientListWithAssessmentData({
    clinicId: clinicianContext.currentClinic ?? null!
  });

  const pendingUsers = useGetPatientListWithAssessmentData({
    clinicId: clinicianContext.currentClinic ?? null!,
    pendingUsers: true
  });

  const isInImPACTClinic = useIsInImPACTClinic();
  /**
   * Combine the data into a single list
   */
  const data = useMemo(
    () =>
      [
        ...(confirmedUsers.data?.pages ?? []).map(_ => ({
          ..._,
          isPending: false
        })),
        ...(pendingUsers.data?.pages ?? []).map(_ => ({
          ..._,
          isPending: true
        }))
      ]
        .flatMap(_ =>
          _.data.data.map(d => ({
            ...d,
            isPending: _.isPending
          }))
        )
        .flat(),
    [confirmedUsers.data, pendingUsers.data]
  );

  const errors = useMemo(
    () =>
      [
        ...(confirmedUsers.data?.pages ?? []),
        ...(pendingUsers.data?.pages ?? [])
      ].flatMap(_ => _.data.errors),
    [confirmedUsers.data, pendingUsers.data]
  );

  const [isLoading, setIsLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const isDataLoading = confirmedUsers.isFetching || pendingUsers.isFetching;

  useQueryErrorNotifier(confirmedUsers, errorList, 'ConfirmedUsers');
  useQueryErrorNotifier(pendingUsers, errorList, 'PendingUsers');

  useEffect(
    () => {
      if (errors?.length !== 0) {
        errors?.forEach(err => {
          console.debug(err);
          return errorList.addError(
            new Error(err.message),
            err.userId,
            `Test-taker (id=${err.userId}) was not shown. This error has been logged and we are working on a fix soon.`
          );
        });
      }
    },
    // eslint-disable-next-line
    [errors, errorList.addError]
  );
  function navigateToPatient(patientId: string) {
    history.push(generatePath(PatientRoutePattern, { patientId: patientId }));
  }

  /**
   * A handler that runs when a patient is selected in the table
   * @param args
   * @returns
   */
  async function onSelectPatient(args: { patientId: string }) {
    try {
      errorList.reset();
      // Don't do anything if the input is empty
      if (args.patientId.length === 0) return;

      // Navigate to the new location
      navigateToPatient(args.patientId);
    } catch (_error) {
      const error = resolveError(_error);
      errorList.addError(
        new Error(
          [
            error.message.trim(),
            `If you think this is an error please wait a few moments and try again, or contact ConcussionRX.`.trim()
          ].join(' ')
        )
      );
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Format the data and apply a search filter
   */
  const {
    filteredData,
    updateSearchQuery,
    showCompletedAssessmentsOnly,
    toggleCompletedAssessmentsOnly
  } = useSearchQueryForPatientList(data);

  const onSearch = useCallback(
    (val: string) => {
      if (val === '') {
        updateSearchQuery(null);
        return;
      }
      updateSearchQuery(val);
    },
    // eslint-disable-next-line
    [updateSearchQuery]
  );

  return (
    <ConfigProvider prefixCls="crx-clinician-home">
      <PrintableRootPage title="Test-taker Assessments" hideTitle>
        <Flex vertical gap={16}>
          <Flex justify={'space-between'} align="end" gap={16}>
            <Flex gap={'small'} align="end">
              <Title style={{ marginBottom: 0 }}>Test-takers</Title>
              <Switch
                checkedChildren="Completed assessments"
                unCheckedChildren="All"
                checked={showCompletedAssessmentsOnly}
                onChange={toggleCompletedAssessmentsOnly}
              />
            </Flex>
            <Flex align="center" gap="small">
              <SendAssessmentButton
                selectedPatients={selectedRows}
                onAssessmentsSent={() => {
                  setSelectedRowKeys([]);
                  setSelectedRows([]);
                  confirmedUsers.refetch();
                  pendingUsers.refetch();
                }}
              />
              {isInImPACTClinic && (
                <AddPatientButton onClick={() => setDrawerVisible(true)} />
              )}
              <div>
                <CrxSearch
                  onSearch={onSearch}
                  placeholder={'Search test-takers'}
                  actionIconAtRight
                />
              </div>
            </Flex>
          </Flex>
          {isDataLoading && (
            <Alert
              closable
              type="info"
              message={`${isDataLoading ? 'Fetching' : 'Fetched'} ${
                (confirmedUsers.data?.pages.length ?? 0) +
                (pendingUsers.data?.pages.length ?? 0)
              } pages`}
            />
          )}

          <AddPatientDrawer
            visible={drawerVisible}
            onClose={() => setDrawerVisible(false)}
            onSuccess={() => {
              confirmedUsers.refetch();
              pendingUsers.refetch();
            }}
          />

          <Table
            loading={isLoading || isDataLoading}
            dataSource={filteredData}
            sortDirections={['descend', 'ascend']}
            columns={COLUMNS}
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys,
              onChange: (keys, rows) => {
                setSelectedRowKeys(keys);
                setSelectedRows(rows as PatientInfo[]);
              },
              selections: [
                Table.SELECTION_ALL,
                Table.SELECTION_INVERT,
                Table.SELECTION_NONE
              ]
            }}
            onRow={record => ({
              onClick: async event => {
                // Do not do anything if the click originated from the row selection checkbox
                if (
                  (event.target as HTMLElement).closest(
                    '.crx-clinician-home-table-selection-column'
                  )
                ) {
                  return;
                }

                if (record.isPending) {
                  showRecordUnavailableModal();
                  return;
                }

                // Start the loading animation
                setIsLoading(true);
                await onSelectPatient({ patientId: record.id });
              }
            })}
            rowHoverable={false}
          />
          {!isDataLoading &&
            data.length === 0 &&
            user?.role === Role.Clinician && (
              <Alert
                type="info"
                message="You are not assigned to any test-takers"
                description="Ask your organisation administrator to be assigned to a test-taker."
              />
            )}

          <ErrorListDisplay closeable errorList={errorList} />
        </Flex>
      </PrintableRootPage>
    </ConfigProvider>
  );
}
